import { publicApiClient } from '@/lib/api/client';
import type { EventScheduleResponse, EventDetailsResponse } from '@/features/events/types/types';
import type { Artist } from '@/features/clubs/types/types';

export const eventScheduleService = {
  /**
   * Fetch event details for a specific event
   * @param eventId - The ID of the event
   * @returns Promise<EventScheduleResponse>
   */
  async getEventDetails(eventId: string): Promise<EventDetailsResponse> {
    try {
      const response = await publicApiClient.get<EventDetailsResponse>(
        `/api/public/v1/event/${eventId}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching event details:', error);
      throw new Error('Failed to fetch event details');
    }
  },

  /**
   * Fetch all event schedule data for a specific event
   * @param eventId - The ID of the event
   * @param limit - Optional limit for testing (defaults to 50)
   * @param offset - Optional offset
   * @returns Promise<EventScheduleResponse>
   */
  async getAllEventSchedules(
    eventId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<EventScheduleResponse> {
    try {
      const response = await publicApiClient.get<EventScheduleResponse>(
        `/api/public/v1/event-schedule/event/${eventId}/all?limit=${limit}&offset=${offset}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching event schedules:', error);
      throw new Error('Failed to fetch event schedules');
    }
  },

  /**
   * Fetch all artists for a specific event
   * GET /api/public/v1/artist/event/:eventID/all
   * @param eventId - The ID of the event
   * @returns Promise<Artist[]>
   */
  async getEventArtists(eventId: string): Promise<Artist[]> {
    try {
      const response = await publicApiClient.get<{ data: Array<{ artist: Artist }> }>(
        `/api/public/v1/artist/event/${eventId}/all`
      );

      // Validate response structure
      if (!response.data || !Array.isArray(response.data)) {
        console.warn('Invalid artists response structure:', response);
        return [];
      }

      // Extract artists from wrapped objects
      return response.data
        .filter(item => item && item.artist)
        .map(item => item.artist);
    } catch (error) {
      console.error('Error fetching event artists:', error);
      return [];
    }
  },
};
